import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import './Card.css';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Padding density. `none` lets the card host edge-to-edge content (tables). */
  padding?: 'none' | 'sm' | 'md';
  /** Lift on hover; only for cards that are themselves a link/action. */
  interactive?: boolean;
}

/**
 * A bordered, softly-elevated surface for genuinely distinct, comparable, or
 * actionable content. Never nest a Card inside a Card — use spacing and
 * dividers for internal grouping. Most content needs no card at all.
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { padding = 'md', interactive = false, className, children, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn('card', `card--p-${padding}`, interactive && 'card--interactive', className)}
      {...rest}
    >
      {children}
    </div>
  );
});

export interface CardHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  /** Right-aligned actions (buttons, menu). */
  actions?: ReactNode;
  className?: string;
}

/** Optional title row for a Card. Keeps the title/description/actions rhythm consistent. */
export function CardHeader({ title, description, actions, className }: CardHeaderProps): JSX.Element {
  return (
    <div className={cn('card__header', className)}>
      <div className="card__heading">
        <h3 className="card__title">{title}</h3>
        {description && <p className="card__description">{description}</p>}
      </div>
      {actions && <div className="card__actions">{actions}</div>}
    </div>
  );
}
